import { useEffect, useState } from "react";
import { Package, Layers, ClipboardCheck, Inbox, Search, Building2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";
import { OrderService } from "../../../../../services/api/order.service";
import LabtestListingTable from "./LabtestListingTable";
import LabtestPrepareOrder from "./LabtestPrepareOrder";
import LabtestViewOrderDetails from "./LabtestViewOrderDetails";
import LabtestUploadReportSidebar from "./LabtestUploadReportSidebar";
import OrderPagination from "../../../../OrderManagement/OrderPagination";
import CancelOrderModal from "../CancelOrderModal";

type OrderTab = "pending" | "ongoing" | "completed" | "unprocessed";

const TABS: OrderTab[] = ["pending", "ongoing", "completed", "unprocessed"];

const TAB_LABELS: Record<OrderTab, string> = {
  pending: "Pending",
  ongoing: "Ongoing",
  completed: "Completed",
  unprocessed: "Unprocessed",
};

const TAB_STATUS_MAP: Record<OrderTab, string> = {
  pending: "Pending",
  ongoing: "Ongoing",
  completed: "Completed",
  unprocessed: "Unprocessed",
};

const TAB_ICONS: Record<OrderTab, React.ReactNode> = {
  pending: <Layers size={14} />,
  ongoing: <Package size={14} />,
  completed: <ClipboardCheck size={14} />,
  unprocessed: <Inbox size={14} />,
};

const AdminLabtestOrder = () => {
  const [activeTab, setActiveTab] = useState<OrderTab>("pending");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, totalPages: 0, totalCount: 0 });
  const [cancelModal, setCancelModal] = useState<{ open: boolean; orderId: number | null; orderNumber?: string }>({
    open: false,
    orderId: null,
    orderNumber: "",
  });
  
  // Filters
  const [searchText, setSearchText] = useState("");
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [refreshCount, setRefreshCount] = useState(0);

  // Navigation Screens
  const [subView, setSubView] = useState<"list" | "prepare" | "view">("list");
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [detailedOrderInfo, setDetailedOrderInfo] = useState<any | null>(null);

  // Upload Report Sidebar State
  const [uploadReportModal, setUploadReportModal] = useState<{ open: boolean; orderId: number; orderNumber: string }>({
    open: false,
    orderId: 0,
    orderNumber: "",
  });

  // Fetch Clinics for Filter Dropdown
  useEffect(() => {
    const fetchClinicsList = async () => {
      try {
        const res = await OrderService.getClinicList();
        if (res?.success && Array.isArray(res.data)) {
          setClinics(res.data);
        }
      } catch (error) {
        console.error("Failed to load clinics", error);
      }
    };
    fetchClinicsList();
  }, []);

  // Fetch orders when tab, page, clinic filter, or search changes
  useEffect(() => {
    fetchOrders();
  }, [activeTab, pagination.page, selectedClinicId, searchText, refreshCount]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await LabService.getAdminLabtestOrderListing(
        pagination.page,
        pagination.pageSize,
        TAB_STATUS_MAP[activeTab],
        selectedClinicId || undefined,
        searchText || undefined
      );
      if (response.success && response.data) {
        setOrders(response.data.ordersList || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.pagination?.totalPages || 0,
          totalCount: response.data.pagination?.totalCount || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching labtest orders:", error);
      toast.error("Failed to fetch labtest orders list.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: OrderTab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSubView("list");
    setSelectedOrder(null);
  };

  const handleAction = async (order: any) => {
    setSelectedOrder(order);
    const status = order.orderStatus?.toLowerCase() || "";
    
    if (status === "admin_pending" || status === "pending_admin") {
      setSubView("prepare");
    } else {
      // Fetch details then show view
      try {
        setLoading(true);
        const details = await LabService.getLabOrderDetails(order.orderId);
        if (details.success && details.data) {
          setDetailedOrderInfo(details.data);
          setSubView("view");
        } else {
          toast.error("Could not fetch order details.");
        }
      } catch (error) {
        toast.error("Failed to retrieve order details.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBackToList = () => {
    setSubView("list");
    setSelectedOrder(null);
    setDetailedOrderInfo(null);
    fetchOrders();
  };

  // Render Sub-Views
  if (subView === "prepare" && selectedOrder) {
    return <LabtestPrepareOrder order={selectedOrder} onBack={handleBackToList} />;
  }

  if (subView === "view" && selectedOrder && detailedOrderInfo) {
    return (
      <LabtestViewOrderDetails
        order={selectedOrder}
        orderDetails={detailedOrderInfo}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="h-full bg-slate-50 rounded-xl flex flex-col overflow-hidden relative">
      {/* Header & Tabs */}
      <div className="px-5 pt-3 pb-0 bg-white border-b border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center text-white">
              <Package size={16} />
            </div>
            <h1 className="text-base font-bold text-slate-800">Labtest Order Management</h1>
          </div>

          {/* Quick Filter Section */}
          <div className="flex items-center gap-3 self-end md:self-auto">
            {/* Clinic Dropdown */}
            <div className="relative flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
              <Building2 size={13} className="text-slate-400" />
              <select
                value={selectedClinicId}
                onChange={(e) => {
                  setSelectedClinicId(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="text-xs bg-transparent border-none outline-none font-bold text-slate-650 cursor-pointer"
              >
                <option value="">All Clinics</option>
                {clinics.map((clinic) => (
                  <option key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search order ID..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-slate-50"
              />
            </div>

            {/* Reset Button */}
            <button
              onClick={() => {
                setSearchText("");
                setSelectedClinicId("");
                setPagination((p) => ({ ...p, page: 1 }));
                setRefreshCount((prev) => prev + 1);
              }}
              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors border-none cursor-pointer"
              title="Reset Filters"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Tabs Bar */}
        <div className="flex">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-xs font-semibold relative whitespace-nowrap transition-colors border-none bg-transparent cursor-pointer ${
                activeTab === tab ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
              }`}
              onClick={() => handleTabChange(tab)}
            >
              <span className="inline-flex items-center gap-1.5">
                {TAB_ICONS[tab]}
                {TAB_LABELS[tab]}
              </span>
              {activeTab === tab && (
                <span className="ml-1.5 px-1.5 py-px rounded-full text-[0.6rem] font-bold bg-emerald-50 text-emerald-600">
                  {pagination.totalCount}
                </span>
              )}
              {activeTab === tab && (
                <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-emerald-500 to-green-600 rounded-t" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Listing Content Body */}
      <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">
        <LabtestListingTable
          orders={orders}
          loading={loading}
          activeTab={activeTab}
          tabLabel={TAB_LABELS[activeTab]}
          onAction={handleAction}
          onCancelOrder={(ord) =>
            setCancelModal({
              open: true,
              orderId: ord.orderId,
              orderNumber: ord.orderNumber,
            })
          }
          currentPage={pagination.page}
          pageSize={pagination.pageSize}
        />
        <OrderPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
        />
      </div>

      {/* Upload Report Drawer */}
      <LabtestUploadReportSidebar
        orderId={uploadReportModal.orderId}
        orderNumber={uploadReportModal.orderNumber}
        isOpen={uploadReportModal.open}
        onClose={() => setUploadReportModal({ open: false, orderId: 0, orderNumber: "" })}
        onSuccess={() => {
          fetchOrders();
          toast.success("List reloaded successfully.");
        }}
      />

      {/* Cancel Order Modal */}
      <CancelOrderModal
        isOpen={cancelModal.open}
        onClose={() => setCancelModal({ open: false, orderId: null })}
        orderId={cancelModal.orderId}
        orderNumber={cancelModal.orderNumber}
        orderType="labtest"
        onSuccess={fetchOrders}
      />
    </div>
  );
};

export default AdminLabtestOrder;